import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const basicObserverCode = `// Core Implementation: The Visibility Guard
const options = {
    root: null, // Relative to the browser viewport
    rootMargin: '0px 0px 400px 0px', // Trigger 400px before entry (pre-fetching)
    threshold: 0.1 // Fire when 10% of the target is visible
};

const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
        if (entry.isIntersecting) {
            console.log("Strategic Load: Element entering view buffer.");
            // 1. Perform action (e.g., Load image, Trigger animation)
            
            // 2. Optimization: Stop observing once the action is complete
            observer.unobserve(entry.target);
        }
    });
}, options);

observer.observe(document.querySelector('.media-heavy-item'));`;

const lazyLoadingCode = `// Performance Engineering: Intelligent Lazy Loading
const lazyLoad = (target) => {
    const io = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const img = entry.target;
                const src = img.getAttribute('data-src');

                img.setAttribute('src', src); // Swapping placeholder for real asset
                img.classList.add('is-loaded');
                
                observer.disconnect(); // Nuclear cleanup for this instance
            }
        });
    });

    io.observe(target);
};`;

const analyticsCode = `// Technical Insight: Viewability Tracking
// Tracking how long a user actually focuses on an advertisement or video
const adObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
        if (entry.intersectionRatio &gt; 0.5) {
            // User has 50% of the ad in view
            startViewTimer();
        } else {
            stopViewTimer();
        }
    });
}, { threshold: [0, 0.5, 1.0] });`;

const JSIntersectionObserver = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Performance & UX</div>
                <h1>Intersection Observer: Visibility Engineering</h1>
                <p className="hero-lead">
                    Master the industry standard for high-performance visibility detection. Learn to build
                    infinite scroll engines, intelligent lazy-loading, and viewport-aware animations
                    without blocking the main thread.
                </p>
            </div>

            <div className="content-section">
                <h2>The Performance Advantage: Off-Thread Observation</h2>
                <p>
                    Historically, detecting if an element was on screen required listening to the \`scroll\`
                    event. This was a performance nightmare, as the scroll event fires dozens of times per
                    second, forcing the browser to perform expensive layout calculations (Reflows) on
                    every tick.
                </p>
                <p>
                    The **Intersection Observer API** moves this logic to the browser's internal engine.
                    It only notifies your JavaScript when a specific **threshold** is met, allowing
                    your application to remain buttery smooth even with thousands of observed elements.
                </p>
                <CodeBlock code={basicObserverCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>The Strategy of rootMargin</h2>
                <p>
                    By default, the observer triggers exactly when the element touches the edge of
                    the screen. However, for a premium user experience, you want content to appear
                    **before** the user scrolls to it.
                </p>
                <ProTip><p>
                        Setting a \`rootMargin\` of \`0px 0px 400px 0px\` effectively expands the
                        "detection zone" 400 pixels below the current viewport. This ensures
                        images are fully loaded and formatted by the time the user's eye reaches them,
                        eliminating "layout shift" and visible loading jars.
                    </p></ProTip>
            </div>

            <div className="content-section">
                <h2>Intelligent Lazy Loading</h2>
                <p>
                    Lazy loading isn't just about images; it's about **resource management**.
                    You can use Intersection Observers to lazy-load heavy sub-components,
                    auto-play/pause videos, or even defer the connection to a heavy
                    third-party chat widget until the user scrolls near the footer.
                </p>
                <CodeBlock code={lazyLoadingCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Accurate Analytics: Viewability Protocols</h2>
                <p>
                    In digital advertising and content analytics, knowing if a user *could* have seen
                    an element is critical. By using an array of thresholds (e.g., \`[0, 0.5, 1.0]\`),
                    you can track exactly when an element enters, crosses the midpoint, and fully
                    occupies the screen.
                </p>
                <CodeBlock code={analyticsCode} language="javascript" />
            </div>

            <KeyFacts><ul>
                    <li>**Performance:** Never use \`getBoundingClientRect()\` inside a scroll listener.</li>
                    <li>**Lifecycle:** Use \`unobserve()\` or \`disconnect()\` as soon as a load is triggered.</li>
                    <li>**Experience:** Use \`rootMargin\` to pre-fetch assets before they are visible.</li>
                    <li>**Stability:** Always provide a \`srcset\` or a low-quality placeholder for lazy images.</li>
                    <li>**Precision:** Use \`threshold\` arrays for complex scroll-linked animations.</li>
                    <li>**Efficiency:** Batch multiple elements into a single observer instance where possible.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSIntersectionObserver;



